import React, { useState } from 'react'
import { useTheme } from '../hooks/useTheme'

const Switch = React.forwardRef(
  (
    {
      checked = false,
      defaultChecked = false,
      disabled = false,
      color = 'primary',
      size = 'medium',
      sx = {},
      className = '',
      onChange,
      ...props
    },
    ref
  ) => {
    const theme = useTheme()
    const [isChecked, setIsChecked] = useState(defaultChecked || checked)

    const handleChange = (event) => {
      if (!disabled) {
        const newValue = !isChecked
        setIsChecked(newValue)
        if (onChange) {
          onChange(event, newValue)
        }
      }
    }

    // Get switch styles based on size and color
    const getSwitchStyles = () => {
      const baseStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
        verticalAlign: 'middle',
        WebkitTapHighlightColor: 'transparent'
      }

      // Size variations
      const sizeStyles = {
        small: {
          width: '40px',
          height: '15px'
        },
        medium: {
          width: '44px',
          height: '24px'
        },
        large: {
          width: '52px',
          height: '28px'
        }
      }

      // Color variations
      const colorStyles = {
        primary: {
          '--switch-color': theme.palette.primary.main || '#1976d2',
          '--switch-color-disabled': '#bdbdbd'
        },
        secondary: {
          '--switch-color': theme.palette.secondary.main || '#9c27b0',
          '--switch-color-disabled': '#bdbdbd'
        },
        error: {
          '--switch-color': '#d32f2f',
          '--switch-color-disabled': '#bdbdbd'
        },
        warning: {
          '--switch-color': '#ed6c02',
          '--switch-color-disabled': '#bdbdbd'
        },
        info: {
          '--switch-color': '#0288d1',
          '--switch-color-disabled': '#bdbdbd'
        },
        success: {
          '--switch-color': '#2e7d32',
          '--switch-color-disabled': '#bdbdbd'
        }
      }

      return {
        ...baseStyles,
        ...sizeStyles[size],
        ...colorStyles[color],
        ...sx
      }
    }

    const getTrackStyles = () => {
      const baseStyles = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '12px',
        backgroundColor: isChecked
          ? 'var(--switch-color)'
          : theme.palette.text.disabled,
        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        opacity: disabled ? 0.38 : 1
      }

      // Size variations
      const sizeStyles = {
        small: {
          borderRadius: '8px'
        },
        medium: {
          borderRadius: '12px'
        },
        large: {
          borderRadius: '14px'
        }
      }

      return {
        ...baseStyles,
        ...sizeStyles[size]
      }
    }

    const getThumbStyles = () => {
      const baseStyles = {
        position: 'absolute',
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
        transition:
          'left 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }

      // Size variations with right padding when checked
      const sizeStyles = {
        small: {
          top: '1px',
          left: isChecked ? 'calc(100% - 12px - 1px)' : '1px',
          width: '12px',
          height: '12px'
        },
        medium: {
          top: '2px',
          left: isChecked ? 'calc(100% - 18px - 4px)' : '2px',
          width: '18px',
          height: '18px'
        },
        large: {
          top: '2px',
          left: isChecked ? 'calc(100% - 22px - 4px)' : '2px',
          width: '22px',
          height: '22px'
        }
      }

      return {
        ...baseStyles,
        ...sizeStyles[size]
      }
    }

    const styles = getSwitchStyles()
    const trackStyles = getTrackStyles()
    const thumbStyles = getThumbStyles()

    return (
      <span
        ref={ref}
        className={className}
        style={styles}
        onClick={handleChange}
        role='switch'
        aria-checked={isChecked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        {...props}
      >
        <span style={trackStyles} />
        <span style={thumbStyles} />
      </span>
    )
  }
)

Switch.displayName = 'Switch'

export default Switch
